/**
 * The 3D view's outline of a selected wall or floor — Lite's PlanSceneBuilder.attachWallHighlight
 * and makeRoomHighlight. A wall wears a solid rim and a faint glow, each a prism grown around one of
 * its panels as the panel stands now, so the outline follows a cutaway, the waist-height walls and
 * the slide between them, and runs round the wall's doors and windows. A floor wears a solid strip
 * and a wide faint one along the inside of its outline, starting where the walls standing on that
 * outline leave the floor visible. Self-lit accent, no shadows, never picked; the root belongs to the
 * scene, never the model.
 */
import * as THREE from 'three';
import type { Vec2, Wall } from '../../core/hp/types';
import { polygonPrismGeometry } from './geometry';
import { outlineMaterial } from './SelectionPresentation';

export type SurfaceSelectionTarget =
  | { kind: 'wall'; node: THREE.Object3D; wall: Wall }
  | { kind: 'room'; node: THREE.Object3D; polygon: readonly Vec2[]; insets?: readonly number[] };

/** Lite's outlineOffset and glowOffset: how far each wall shell stands off the panel, metres. */
const WALL_SHELLS = [{ offset: .018, opacity: 1 }, { offset: .06, opacity: .22 }] as const;
/** Lite's curbs: how far each strip reaches into the room and how high over the floor it lies, metres. */
const FLOOR_STRIPS = [{ width: .05, y: .008, opacity: 1 }, { width: .22, y: .006, opacity: .25 }] as const;
/** A sharp corner's strip may reach this many widths along its bisector, no further. */
const MITER_LIMIT = 3;

type Point = [number, number];

export class SurfaceSelection {
  readonly root = new THREE.Group();
  private key = '';
  private meshes: THREE.Mesh[] = [];
  private materials: { wall: THREE.MeshStandardMaterial[]; floor: THREE.MeshStandardMaterial[] } | null = null;
  private disposed = false;

  constructor(private readonly color?: string) {
    this.root.name = 'surface-selection';
    // The contact-shadow pass and the material preview skip anything carrying this flag.
    this.root.userData.selectionPresentation = true;
    this.root.matrixAutoUpdate = false;
    this.root.visible = false;
  }

  update(target: SurfaceSelectionTarget | null, enabled: boolean): void {
    if (this.disposed) return;
    if (!target) { this.clear(); return; }
    target.node.updateWorldMatrix(true, true);
    const key = keyOf(target);
    if (key !== this.key) { this.rebuild(target); this.key = key; }
    this.root.visible = enabled && this.meshes.length > 0 && visibleAncestors(target.node);
    // Wall shells are built in world space; floor strips in the floor's own frame.
    this.root.matrix.identity();
    if (target.kind === 'room') this.root.matrix.copy(target.node.matrixWorld);
    if (this.root.parent) {
      this.root.parent.updateWorldMatrix(true, false);
      this.root.matrix.premultiply(new THREE.Matrix4().copy(this.root.parent.matrixWorld).invert());
    }
    this.root.updateMatrixWorld(true);
  }

  /** What the outline is made of now, for checks: shell and strip counts and the world-space box. */
  describe(): { visible: boolean; meshes: number; bounds: THREE.Box3 } {
    const bounds = new THREE.Box3();
    if (this.meshes.length) bounds.setFromObject(this.root);
    return { visible: this.root.visible, meshes: this.meshes.length, bounds };
  }

  private rebuild(target: SurfaceSelectionTarget): void {
    this.removeMeshes();
    const materials = this.materials ??= {
      wall: WALL_SHELLS.map(shell => outlineMaterial(shell.opacity, 'select', this.color)),
      floor: FLOOR_STRIPS.map(strip => {
        const material = outlineMaterial(strip.opacity, 'select', this.color);
        // The strips lie a few millimetres over the floor; the offset keeps them there at any distance.
        material.polygonOffset = true; material.polygonOffsetFactor = -2; material.polygonOffsetUnits = -2;
        return material;
      }),
    };
    if (target.kind === 'wall') {
      target.node.traverseVisible(node => {
        if (!(node instanceof THREE.Mesh) || node.userData.kind !== 'wall') return;
        WALL_SHELLS.forEach((shell, i) => {
          const geometry = grownPanel(node, target.wall, shell.offset);
          if (geometry) this.addMesh(geometry, materials.wall[i], i);
        });
      });
    } else {
      FLOOR_STRIPS.forEach((strip, i) => {
        const geometry = floorStrip(target.polygon, target.insets ?? [], strip.width, strip.y);
        if (geometry) this.addMesh(geometry, materials.floor[i], i);
      });
    }
  }

  private addMesh(geometry: THREE.BufferGeometry, material: THREE.Material, order: number): void {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'selection-shell'; mesh.userData.selectionPresentation = true;
    mesh.castShadow = false; mesh.receiveShadow = false; mesh.renderOrder = order;
    mesh.raycast = () => {}; // Defensive even if a consumer raycasts the whole scene.
    this.root.add(mesh); this.meshes.push(mesh);
  }

  private removeMeshes(): void {
    for (const mesh of this.meshes) { mesh.removeFromParent(); mesh.geometry.dispose(); }
    this.meshes = [];
  }

  clear(): void {
    this.removeMeshes(); this.key = ''; this.root.visible = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.clear();
    for (const material of [...this.materials?.wall ?? [], ...this.materials?.floor ?? []]) material.dispose();
    this.materials = null; this.root.removeFromParent(); this.disposed = true;
  }
}

function visibleAncestors(target: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = target; node; node = node.parent) if (!node.visible) return false;
  return true;
}

/** Changes whenever the outline would: another wall or room, a panel cut or rebuilt, a panel shown or hidden. */
function keyOf(target: SurfaceSelectionTarget): string {
  if (target.kind === 'room') return `room:${target.node.uuid}:${target.polygon.map(p => `${p.x},${p.z}`).join(';')}:${(target.insets ?? []).join(',')}`;
  const parts = [`wall:${target.node.uuid}:${target.wall.start.x},${target.wall.start.z},${target.wall.end.x},${target.wall.end.z}`];
  target.node.traverseVisible(node => {
    if (!(node instanceof THREE.Mesh) || node.userData.kind !== 'wall') return;
    const geometry = node.geometry, position = geometry.getAttribute('position');
    parts.push(`${geometry.uuid}:${position?.version ?? 0}:${geometry.index?.version ?? 0}:${node.matrixWorld.elements.join(',')}`);
  });
  return parts.join('|');
}

/**
 * A wall panel is a vertical convex prism: its outline on the floor, grown by `offset` along the wall
 * and across it, stood from its bottom to its top, each widened by `offset`. The outline is read off
 * the drawn triangles, so a panel cut lower is outlined where it now ends.
 */
function grownPanel(mesh: THREE.Mesh, wall: Wall, offset: number): THREE.BufferGeometry | null {
  const geometry = mesh.geometry, positions = geometry.getAttribute('position'), index = geometry.index;
  if (!positions?.count) return null;
  const first = geometry.drawRange.start, last = Math.min(index?.count ?? positions.count, first + geometry.drawRange.count);
  const dir = wall.dir, normal = { x: -dir.z, z: dir.x }, p = new THREE.Vector3();
  const corners: Point[] = [];
  let bottom = Infinity, top = -Infinity;
  for (let i = first; i < last; i++) {
    p.fromBufferAttribute(positions, index ? index.getX(i) : i).applyMatrix4(mesh.matrixWorld);
    const dx = p.x - wall.start.x, dz = p.z - wall.start.z;
    const u = dx * dir.x + dz * dir.z, v = dx * normal.x + dz * normal.z;
    corners.push([u - offset, v - offset], [u + offset, v - offset], [u + offset, v + offset], [u - offset, v + offset]);
    bottom = Math.min(bottom, p.y); top = Math.max(top, p.y);
  }
  if (!(top - bottom > 1e-4)) return null;
  const outline = convexHull(corners);
  if (outline.length < 3) return null;
  const polygon = outline.map(([u, v]) => ({ x: wall.start.x + dir.x * u + normal.x * v, z: wall.start.z + dir.z * u + normal.z * v }));
  return polygonPrismGeometry(polygon, bottom - offset, top + offset, wall.start, wall.end);
}

/** Andrew's monotone chain; counter-clockwise, no repeated or collinear points. */
function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point, a: Point, b: Point) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const chain = (list: Point[]) => {
    const result: Point[] = [];
    for (const point of list) {
      while (result.length >= 2 && cross(result[result.length - 2], result[result.length - 1], point) <= 1e-12) result.pop();
      result.push(point);
    }
    result.pop();
    return result;
  };
  return [...chain(sorted), ...chain(sorted.reverse())];
}

/**
 * How far into the room each outline edge's floor comes into view: half the thickness of a wall
 * drawn along that edge — a drawn room's outline runs down the walls' middles — and nothing where
 * the outline already follows the walls' faces, as imported rooms may.
 */
export function roomEdgeInsets(polygon: readonly Vec2[], walls: readonly Wall[]): number[] {
  return polygon.map((a, i) => {
    const b = polygon[(i + 1) % polygon.length], length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-6) return 0;
    const d = { x: (b.x - a.x) / length, z: (b.z - a.z) / length };
    let inset = 0;
    for (const wall of walls) {
      if (wall.hidden || Math.abs(d.x * wall.dir.z - d.z * wall.dir.x) > .01) continue;
      if (Math.abs((wall.start.x - a.x) * d.z - (wall.start.z - a.z) * d.x) > .02) continue;
      const s0 = (wall.start.x - a.x) * d.x + (wall.start.z - a.z) * d.z, s1 = (wall.end.x - a.x) * d.x + (wall.end.z - a.z) * d.z;
      if (Math.min(Math.max(s0, s1), length) - Math.max(Math.min(s0, s1), 0) < Math.min(.05, length / 2)) continue;
      inset = Math.max(inset, wall.thickness / 2);
    }
    return inset;
  });
}

/**
 * Lite's curb as one ring: the room's outline moved in by each edge's inset, and again by `width`,
 * mitred at the corners so the faint strip never doubles up. The triangles face down — the shared
 * back-face outline material then shows them from above without a second shader.
 */
function floorStrip(polygon: readonly Vec2[], insets: readonly number[], width: number, y: number): THREE.BufferGeometry | null {
  let edges = polygon.map((p, i) => ({ p, inset: insets[i] ?? 0 })).filter(({ p }, i) => {
    const q = polygon[(i + 1) % polygon.length];
    return Math.hypot(q.x - p.x, q.z - p.z) > 1e-6;
  });
  if (edges.length < 3) return null;
  const area = edges.reduce((sum, { p }, i) => { const q = edges[(i + 1) % edges.length].p; return sum + p.x * q.z - q.x * p.z; }, 0);
  if (Math.abs(area) < 1e-9) return null;
  // Reversed, edge i runs from the old i + 1 to the old i: it keeps the inset of the old edge i.
  if (area < 0) edges = edges.map((_, i) => ({ p: edges[(edges.length - i) % edges.length].p, inset: edges[edges.length - 1 - i].inset }));
  const points = edges.map(edge => edge.p), count = points.length;
  // With the outline counter-clockwise (x across, z up the page), the room lies left of every edge.
  const inward = points.map((p, i) => {
    const q = points[(i + 1) % count], length = Math.hypot(q.x - p.x, q.z - p.z);
    return { x: -(q.z - p.z) / length, z: (q.x - p.x) / length };
  });
  // The corner where the edge before and the edge after, each moved in by its own distance, meet.
  const corner = (i: number, extra: number) => {
    const before = (i - 1 + count) % count, a = inward[before], b = inward[i];
    const da = edges[before].inset + extra, db = edges[i].inset + extra, det = a.x * b.z - a.z * b.x;
    const p = points[i], limit = MITER_LIMIT * Math.max(da, db);
    if (Math.abs(det) < 1e-6) return { x: p.x + b.x * db, z: p.z + b.z * db };
    let x = (da * b.z - a.z * db) / det, z = (a.x * db - da * b.x) / det;
    const reach = Math.hypot(x, z);
    if (reach > limit) { x *= limit / reach; z *= limit / reach; }
    return { x: p.x + x, z: p.z + z };
  };
  const outer = points.map((_, i) => corner(i, 0)), inner = points.map((_, i) => corner(i, width));
  const positions: number[] = [];
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count;
    const a = outer[i], b = outer[j], c = inner[j], d = inner[i];
    positions.push(a.x, y, a.z, b.x, y, b.z, c.x, y, c.z, a.x, y, a.z, c.x, y, c.z, d.x, y, d.z);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}
